import React, { useEffect, useRef } from 'react';
import {
    Modal,
    ModalHeader,
    UncontrolledCollapse,
    Badge,
    ModalBody,
    Card,
    CardHeader,
    CardBody,
    Button,
    Nav,
    NavItem,
    NavLink,
    Alert,
} from 'reactstrap';
import SimpleBar from 'simplebar-react';
import { SrxApi } from '../api/SrxApi';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Icon } from '@iconify/react';
import { useContext } from 'react';
import { CurrentTimeContext } from '../contexts/CurrentTimeContext';
import checkBold from '@iconify/icons-mdi/check-bold';
import closeThick from '@iconify/icons-mdi/close-thick';
import clockMinusOutline from '@iconify/icons-mdi/clock-minus-outline';
import numeric1CircleOutline from '@iconify/icons-mdi/numeric-1-circle-outline';
import { GrChatOption } from 'react-icons/gr';
import { UserContext } from '../contexts/UserContext';
import generatePDF from '../utility-services/reportGenerator';
import printerIcon from '@iconify/icons-mdi/printer';
import emailOutline from '@iconify/icons-mdi/email-outline';
import emailOpenOutline from '@iconify/icons-mdi/email-open-outline';
import useUpdateAlert from '../custom-hooks/useUpdateAlert';
import downloadIcon from '@iconify/icons-mdi/download';
import { SelectedPharmaciesContext } from '../contexts/SelectedPharmaciesContext';
import { SelectedFacilitiesContext } from '../contexts/SelectedFacilitiesContext';
import TableRowDetailBody from './TableRowDetailBody';
import {
    ActionButton,
    ActionCommentModal,
    ActionSendModal,
    ActionDoNotSendModal,
    ActionQuestionModal,
    ActionLimitedApprovalModal,
} from './TableRowDetailContent/ActionControls';
import { decode } from 'he';
import { printAlertDetailTicket, printNarcoticsRefillTicket } from '../utility-services/reportGenerator';
import {
    checkAlertStatus as sharedCheckAlertStatus,
    checkQuantity as sharedCheckQuantity,
    checkSupply as sharedCheckSupply,
    capitalizeFirstLetter as sharedCapitalizeFirstLetter,
    generateFileName,
    getDateAndTime as sharedGetDateAndTime,
    getDateAndTimeStamp as sharedGetDateAndTimeStamp,
    getDateTime as sharedGetDateTime,
    getLastAlertStage as sharedGetLastAlertStage,
    getUserActionLabelForHeader as sharedGetUserActionLabelForHeader,
    parseTemporal as sharedParseTemporal,
    getAlertCommentsForDisplay,
} from './TableRowDetailSharedUtils';
import { submitUserAction } from './TableRowDetailSharedUserAction';
import { getActionSentTimeMs, isActionFromCurrentUser } from './TableRowDetailContent/actionUtils';

const TableRowDetailModal = ({ isOpen, toggle, alertInfo }) => {
    const { alert: alertFromInfo, stateName, name, category, tab, requestActionPermissionWidgetName } = alertInfo;
    const [alert, setAlert] = useState(alertFromInfo);

    useEffect(() => {
        setAlert(alertFromInfo);
    }, [alertFromInfo]);

    const WIDGET_ACTION_PERMISSION_KEY = {
        highCost: { ask: 'canActionHighCostAsk', approve: 'canActionHighCostApprove', deny: 'canActionHighCostDeny', limit: 'canActionHighCostLimit', once: 'canActionHighCostOnce' },
        highcostalerts: { ask: 'canActionHighCostAlertAsk', approve: 'canActionHighCostAlertApprove', deny: 'canActionHighCostAlertDeny', limit: 'canActionHighCostAlertLimit' },
        openBatch: { ask: 'canActionOpenBatchAsk' },
        nonCovered: { ask: 'canActionNonCoveredAsk' },
        priorAuth: { ask: 'canActionPriorAuthAsk' },
        theraInt: { ask: 'canActionTheraIntAsk' },
        admissionMeds: { ask: 'canActionAdmissionMedsAsk' },
        refillTooSoon: { ask: 'canActionRefillTooSoonAsk' },
        pumpPickup: { ask: 'canActionPumpPickupAsk' },
        narcoticsRefill: { ask: 'canActionNarcoticsRefillAsk' },
        alfNewScript: { ask: 'canActionAlfNewScriptAsk' },
    };
    const ACTION_TO_PERMISSION_TIER = { ask: 'ask', approve: 'approve', deny: 'deny', submitlimit: 'limit', approveonce: 'once' };

    const checkActionPermissions = (requestActions, widgetName, pharmacies, pharmId, facID) => {
        return requestActions.filter(({ action }) => {
            if (action === 'limitedApproval') {
                return user.userType === 'PharmacyUser' || user.userType === 'FacilityUser';
            }

            const permissionKey = WIDGET_ACTION_PERMISSION_KEY[widgetName]?.[ACTION_TO_PERMISSION_TIER[action]];
            if (!permissionKey) return false;

            if (user.userType === 'PharmacyUser') {
                return !!(
                    pharmacies?.find((pharm) => pharm.pharmID === pharmId)?.userPermissions?.[permissionKey] &&
                    pharmacies?.find((pharm) => pharm.pharmID === pharmId)?.pharmPermissions?.[permissionKey] &&
                    facilities?.find((facility) => facility.facID === facID)?.permissions?.[permissionKey]
                );
            } else if (user.userType === 'FacilityUser') {
                return !!(
                    pharmacies?.find((pharm) => pharm.pharmID === pharmId)?.pharmPermissions?.[permissionKey] &&
                    facilities?.find((facility) => facility.facID === facID)?.permissions?.[permissionKey]
                );
            }
            return false;
        });
    };
    const tableRowDetailPagelink = () => {
        const domain = window.location.origin;
        const categoryMap = {
            highCost: 'authorization',
            highcostalerts: 'highcostalerts',
            clarifications: 'clarifications',
            openbatch: 'openbatch',
            priorAuth: 'prior-authorization-medication',
            nonCovered: 'non-covered',
            therapeuticInt: 'therapeutic-interchange',
            admissionMeds: 'admission-medication-review',
            pumpPickup: 'pumpPickup',
            refillTooSoon: 'refillTooSoon',
            narcoticsRefill: 'narcoticsRefill',
            alfNewScript: 'alfNewScript',
            alfCostResponse: 'alfCostResponse',
            unavailableMedicationAlerts: 'unavailableMedicationAlerts',
        };

        const path = categoryMap[category];
        if (!path) {
            throw new Error('Invalid category');
        }

        return `${domain}/${path}/${category === 'admissionMeds' ? alert?.admissionMedsGroupID : (alert?.rxNo ?? alert?.rxNumber)}`;
    };

    const { user, setUser } = useContext(UserContext);

    const { pharmacies } = useContext(SelectedPharmaciesContext);

    const { facilities } = useContext(SelectedFacilitiesContext);

    const [activeTab, setActiveTab] = useState('detail');

    function parseTemporal(date) {
        return sharedParseTemporal(date);
    }

    function getDateTime(date) {
        return sharedGetDateTime(date);
    }

    function getDateAndTime(date) {
        return sharedGetDateAndTime(date);
    }

    function getDateAndTimeStamp(date) {
        return sharedGetDateAndTimeStamp(date);
    }

    function capitalizeFirstLetter(word) {
        return sharedCapitalizeFirstLetter(word);
    }

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            background: '#fff',
            borderColor: '#9e9e9e',
            minHeight: '31.94px',
            height: '31.94px',
            boxShadow: state.isFocused ? null : null,
        }),

        valueContainer: (provided, state) => ({
            ...provided,
            height: '31.94px',
            padding: '0 6px',
        }),

        input: (provided, state) => ({
            ...provided,
            margin: '0px',
        }),
        indicatorSeparator: (state) => ({
            display: 'none',
        }),
        indicatorsContainer: (provided, state) => ({
            ...provided,
            height: '31.94px',
        }),
    };

    const [loadingUserAction, setLoadingUserAction] = useState(false);

    const [actionComment, setActionComment] = useState('');
    const [limitDays, setLimitDays] = useState(null);

    const appendSubmittedAction = (submittedAction) => {
        setAlert((currentAlert) => {
            const currentActions = currentAlert?.requestedActions || [];
            const incomingActionId = submittedAction?.requestActionID ?? submittedAction?.requestactionid;
            const incomingSentTime = getActionSentTimeMs(submittedAction);
            const incomingComment = (submittedAction?.actionComment || '').trim().toLowerCase();
            const incomingType = (submittedAction?.action || '').trim().toLowerCase();

            const duplicateExists = currentActions.some((existingAction) => {
                const existingActionId = existingAction?.requestActionID ?? existingAction?.requestactionid;
                if (incomingActionId && existingActionId && String(incomingActionId) === String(existingActionId)) {
                    return true;
                }

                const existingComment = (existingAction?.actionComment || '').trim().toLowerCase();
                const existingType = (existingAction?.action || '').trim().toLowerCase();
                if (incomingComment !== existingComment || incomingType !== existingType) {
                    return false;
                }

                const bothFromCurrentUser =
                    isActionFromCurrentUser(existingAction, user) && isActionFromCurrentUser(submittedAction, user);
                if (!bothFromCurrentUser) {
                    return false;
                }

                const existingSentTime = getActionSentTimeMs(existingAction);
                if (!incomingSentTime || !existingSentTime) {
                    return true;
                }

                return Math.abs(incomingSentTime - existingSentTime) <= 120000;
            });

            if (duplicateExists) {
                return currentAlert;
            }

            return {
                ...currentAlert,
                requestedActions: [...currentActions, submittedAction],
            };
        });
    };

    const handleUserAction = async (action, actionComment, widget, limitDays = null, username = null) => {
        await submitUserAction({
            action,
            actionComment,
            widget,
            limitDays,
            username,
            alert,
            user,
            setUser,
            setLoadingUserAction,
            setActionComment,
            toggleCommentModal,
            toggleQuestionModal,
            toggleLimitedApprovalModal,
            toggleSendModal,
            toggleDoNotSendModal,
            onActionSubmitted: appendSubmittedAction,
        });
    };

    const getUserActionLabelForHeader = (action = '') => sharedGetUserActionLabelForHeader(action);

    const { currentTime } = useContext(CurrentTimeContext);

    const [commentModalInfo, setCommentModalInfo] = useState({
        isOpen: false,
        action: '',
    });

    const [questionModalInfo, setQuestionModalInfo] = useState({
        isOpen: false,
        action: '',
    });

    const [limitedApprovalModalInfo, setLimitedApprovalModalInfo] = useState({
        isOpen: false,
        action: '',
    });

    const [sendModalInfo, setSendModalInfo] = useState({
        isOpen: false,
        action: '',
    });

    const [doNotSendModalInfo, setDoNotSendModalInfo] = useState({
        isOpen: false,
        action: '',
    });

    function openCommentModal(action, color, label) {
        setCommentModalInfo({
            isOpen: true,
            action,
            color,
            label,
        });
    }

    function openSendModal(action, color, label) {
        setSendModalInfo({
            isOpen: true,
            action,
            color,
            label,
        });
    }

    function openDoNotSendModal(action, color, label) {
        setDoNotSendModalInfo({
            isOpen: true,
            action,
            color,
            label,
        });
    }

    function openQuestionModal(action, color, label) {
        setQuestionModalInfo({
            isOpen: true,
            action,
            color,
            label,
        });
    }

    function openLimitedApprovalModal(action, color, label) {
        setLimitedApprovalModalInfo({
            isOpen: true,
            action,
            color,
            label,
        });
    }

    function toggleCommentModal() {
        setCommentModalInfo({
            isOpen: !commentModalInfo.isOpen,
            action: '',
        });
    }

    function toggleSendModal() {
        setSendModalInfo({
            isOpen: !sendModalInfo.isOpen,
            action: '',
        });
    }

    function toggleDoNotSendModal() {
        setDoNotSendModalInfo({
            isOpen: !doNotSendModalInfo.isOpen,
            action: '',
        });
    }

    function toggleQuestionModal() {
        setQuestionModalInfo({
            isOpen: !questionModalInfo.isOpen,
            action: '',
        });
    }

    function toggleLimitedApprovalModal() {
        setLimitedApprovalModalInfo({
            isOpen: !limitedApprovalModalInfo.isOpen,
            action: '',
        });
    }

    const [showNonMediprocityUserAlert, setShowNonMediprocityUserAlert] = useState();

    const handleActionButtons = (action) => {
        if (!user.mediprocityUser) return setShowNonMediprocityUserAlert(true);
        if (action.toLowerCase() === 'approve') {
            openCommentModal(action, 'success', 'Approve');
        } else if (action.toLowerCase() === 'deny') {
            openCommentModal(action, 'danger', 'Deny');
        } else if (action.toLowerCase() === 'ask') {
            openQuestionModal(action, 'warning', 'Discuss');
        } else if (action.toLowerCase() === 'approveonce') {
            openCommentModal(action, 'secondary', 'Approve Once');
        } else if (action.toLowerCase() === 'submitlimit') {
            openLimitedApprovalModal(action, 'warning', 'Limit Approval');
        } else if (action.toLowerCase() === 'send') {
            openSendModal(action, 'success', 'Send');
        } else if (action.toLowerCase() === 'donotsend') {
            openDoNotSendModal(action, 'danger', 'Do Not Send');
        } else if (action.toLowerCase() === 'limitedapproval') {
            openCommentModal(action, 'success', 'Approve until Refill Date');
        }
    };
    const discussChatIcon = <GrChatOption size={22} />;

    const highCostActionButtons = [
        {
            icon: checkBold,
            title: 'Approve',
            action: 'approve',
            disable: false,
        },
        {
            icon: closeThick,
            title: 'Deny',
            action: 'deny',
            disable: false,
        },
        {
            icon: clockMinusOutline,
            title: 'Limit Days',
            action: 'submitlimit',
            disable: false,
        },
        {
            icon: numeric1CircleOutline,
            title: 'Approve Once',
            action: 'approveonce',
            disable: false,
        },
        {
            icon: discussChatIcon,
            title: 'Discuss',
            action: 'ask',
            disable: false,
        },
    ];

    const highCostAlertsActionButtons = [
        {
            icon: checkBold,
            title: 'Approve',
            action: 'approve',
            disable: false,
        },
        {
            icon: closeThick,
            title: 'Deny',
            action: 'deny',
            disable: false,
        },
        {
            icon: clockMinusOutline,
            title: 'Limit Days',
            action: 'submitlimit',
            disable: false,
        },
        {
            icon: discussChatIcon,
            title: 'Discuss',
            action: 'ask',
            disable: false,
        },
    ];

    const clarificationsActionButtons = [
        {
            icon: discussChatIcon,
            title: 'Discuss',
            action: 'ask',
            disable: false,
        },
    ];

    const remainingWidgetsActionButtons = [
        {
            icon: discussChatIcon,
            title: 'Discuss',
            action: 'ask',
            disable: false,
        },
    ];

    const archivedActionButtons = [
        {
            icon: discussChatIcon,
            title: 'Discuss',
            action: 'ask',
            disable: false,
        },
    ];

    const admissionMedicationReviewActionButtons = [
        {
            icon: discussChatIcon,
            title: 'Discuss',
            action: 'ask',
            disable: false,
        },
    ];

    const RTSActionButtons = [
        {
            icon: clockMinusOutline,
            title: 'Approve Until Refill Date',
            action: 'limitedApproval',
            disable: false,
        },
        {
            icon: discussChatIcon,
            title: 'Discuss',
            action: 'ask',
            disable: false,
        },
    ];

    const pumpPickupActionButtons = [
        {
            icon: discussChatIcon,
            title: 'Discuss',
            action: 'ask',
            disable: false,
        },
    ];

    const narcoticsRefillActionButtons = [
        {
            icon: discussChatIcon,
            title: 'Discuss',
            action: 'ask',
            disable: false,
        },
    ];

    /** ALF reuses narcotics action permissions until widget_alfNewScript_* is configured on the backend. */
    const getNarcoticsStyleDiscussButtons = (pharmID, facID) => {
        const permissionWidget = category === 'alfNewScript' ? 'narcoticsRefill' : requestActionPermissionWidgetName;
        const permitted = checkActionPermissions(
            narcoticsRefillActionButtons,
            permissionWidget,
            pharmacies,
            pharmID,
            facID
        );
        return permitted?.length ? permitted : narcoticsRefillActionButtons;
    };

    const discussActionWidgetName = category === 'alfNewScript' ? 'alfNewScript' : 'NarcoticsRefill';

    const [firstTimeScrolling, setFirstTimeScrolling] = useState(true);

    useEffect(() => {
        if (activeTab === 'actions' && firstTimeScrolling) {
            setFirstTimeScrolling(false);
            let container = document.querySelector('.action-display-simplebar-container .simplebar-content-wrapper');
            container && container.scrollTo({ top: container.scrollHeight, behavior: 'auto' });
        }
    }, [activeTab]);

    useEffect(() => {
        if (activeTab === 'actions' && !firstTimeScrolling) {
            let container = document.querySelector('.action-display-simplebar-container .simplebar-content-wrapper');
            container && container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        }
    }, [alertInfo]);

    const { updateReadStatus, updateArchivedReadStatus } = useUpdateAlert();

    const printWidgetDetailDialog = () => {
        const doc = printAlertDetailTicket(alert, name, category);
        window.open(doc.output('bloburl'), '_blank');
    };

    const changeReadStatus = async (status) => {
        function returnUrl() {
            switch (category) {
                case 'highCost':
                    return '/highcost_readByFaci';
                case 'highcostalerts':
                    return '/highcostalerts_readByFaci';
                case 'clarifications':
                    return '/openbatch_readByFaci';
                case 'openbatch':
                    return '/openbatch_readByFaci';
                case 'priorAuth':
                    return '/priorauth_readByFaci';
                case 'nonCovered':
                    return '/noncovered_readByFaci';
                case 'therapeuticInt':
                    return '/therapeuticint_readByFaci';
                case 'admissionMeds':
                    return '/admissionmeds_readByFaci';
                case 'refillTooSoon':
                    return '/refilltoosoon_readByFaci';
                case 'pumpPickup':
                    return '/pumppickup_readByFaci';
                case 'narcoticsRefill':
                    return '/narcoticsrefill_readByFaci';
                case 'alfNewScript':
                    return '/alfnewscriptneeded_readByFaci';
                case 'alfCostResponse':
                    return '/alfnonactionordenial_readByFaci';
                case 'unavailableMedicationAlerts':
                    return '/unavailablemedicationalerts_readByFaci';
            }
        }

        let id;
        switch (category) {
            case 'admissionMeds':
                id = alert?.admissionMedsGroupID;
                break;
            default:
                id = alert?._internalID;
        }

        SrxApi.post(returnUrl(), {
            alertId: id,
            readByFaci: status,
        })
            .then((res) => {
                tab === 'archive'
                    ? updateArchivedReadStatus(status, alert, category)
                    : updateReadStatus(status, alert, category);
            })
            .catch((err) => console.log(err.message));
    };

    useEffect(() => {
        if (alert && !alert.readByFaci && user.userType !== 'PharmacyUser' && checkFacilityPermissionAutomatically())
            changeReadStatus(true);
    }, []);

    const checkFacilityPermissionManually = () => {
        const faci = alert?.facID ?? alert?.facilityId;
        let facilityId = faci;
        let facilityList = facilities;
        return facilityList?.find((facility) => {
            if (facilityId === facility.facID) {
                return (
                    facility.permissions?.['canMarkAlertReadManually'] &&
                    facility.permissions?.['canMarkAlertUnreadManually']
                );
            }
        });
    };

    const checkFacilityPermissionAutomatically = () => {
        const faci = alert?.facID ?? alert?.facilityId;
        let facilityId = faci;
        let facilityList = facilities;
        return facilityList?.find((facility) => {
            if (facilityId === facility.facID) {
                return (
                    facility.permissions?.['canMarkAlertReadAutomatically'] &&
                    facility.permissions?.['canMarkAlertUnreadAutomatically']
                );
            }
        });
    };

    function getLastAlertStage(alert) {
        return sharedGetLastAlertStage(alert);
    }
    function checkAlertStatus(alert) {
        return sharedCheckAlertStatus(alert);
    }
    const checkQuantity = (alert) => {
        return sharedCheckQuantity(alert);
    };
    const checkSupply = (alert) => {
        return sharedCheckSupply(alert);
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} modalClassName="widget-detail-print-modal">
            <ModalHeader
                toggle={toggle}
                className="gradient-modal d-flex"
                style={{
                    background: 'linear-gradient(90deg, #452f69 0%, #452f69 50%, #000 90%)',
                    color: 'white',
                }}>
                {name
                    ?.split(' ')
                    .map((word) => {
                        return word[0].toUpperCase() + word.substring(1);
                    })
                    .join(' ')}
                {stateName === 'highCostList' && (
                    <Badge color={'secondary'} className="ml-1">
                        {getUserActionLabelForHeader(alert?.srxStatus)}
                    </Badge>
                )}
                {
                    <Button
                        title={alert?.readByFaci ? 'Mark as unread' : 'Mark as read'}
                        hidden={!(checkFacilityPermissionManually() || category === 'alfNewScript')}
                        onClick={() => {
                            checkFacilityPermissionManually();
                            changeReadStatus(!alert?.readByFaci);
                            if (alert?.readByFaci) toggle();
                        }}
                        className="link-button p-0 mb-1 ml-1"
                        color="link"
                        style={{ color: 'white', fontSize: '26px' }}>
                        <Icon icon={alert?.readByFaci ? emailOutline : emailOpenOutline} />
                    </Button>
                }
                {alert?.rxNo !== undefined ||
                alert?.admissionMedsGroupID !== undefined ||
                alert?.rxNumber !== undefined ? (
                    <>
                        <Link
                            title="Copy Link"
                            onClick={(event) => {
                                navigator.clipboard.writeText(`${tableRowDetailPagelink(category)}`);
                                toast.info('URL copied to clipboard');
                                if (event.ctrlKey) {
                                    const newTab = window.open(`${tableRowDetailPagelink(category)}`, '_blank');
                                    const _ = newTab?.focus();
                                }
                            }}
                            className="link-button p-0 mb-1 ml-1"
                            color="link"
                            to="#"
                            style={{ color: 'white', fontSize: '26px' }}>
                            <Icon icon="mdi:link-variant" style={{ color: 'white', fontSize: '26px' }} />
                        </Link>
                    </>
                ) : null}
                <Button
                    title="Print dialog"
                    className="link-button p-0 mb-1 ml-1 widget-detail-print-hide"
                    color="link"
                    style={{ color: 'white', fontSize: '26px' }}
                    onClick={printWidgetDetailDialog}>
                    <Icon icon={printerIcon} />
                </Button>
            </ModalHeader>
            <ModalBody>
                {' '}
                <TableRowDetailBody
                    alert={alert}
                    stateName={stateName}
                    category={category}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    requestActionPermissionWidgetName={requestActionPermissionWidgetName}
                    pharmacies={pharmacies}
                    getPermittedActions={(requestActions, widgetName, item) =>
                        checkActionPermissions(requestActions, widgetName, pharmacies, item.pharmID, item.facID)
                    }
                    getDateTime={getDateTime}
                    getDateAndTime={getDateAndTime}
                    getDateAndTimeStamp={getDateAndTimeStamp}
                    capitalizeFirstLetter={capitalizeFirstLetter}
                    getUserActionLabelForHeader={getUserActionLabelForHeader}
                    getLastAlertStage={getLastAlertStage}
                    checkAlertStatus={checkAlertStatus}
                    checkQuantity={checkQuantity}
                    checkSupply={checkSupply}
                    showNonMediprocityUserAlert={showNonMediprocityUserAlert}
                    handleActionButtons={handleActionButtons}
                    commentModalInfo={commentModalInfo}
                    questionModalInfo={questionModalInfo}
                    limitedApprovalModalInfo={limitedApprovalModalInfo}
                    sendModalInfo={sendModalInfo}
                    doNotSendModalInfo={doNotSendModalInfo}
                    toggleCommentModal={toggleCommentModal}
                    toggleQuestionModal={toggleQuestionModal}
                    toggleLimitedApprovalModal={toggleLimitedApprovalModal}
                    toggleSendModal={toggleSendModal}
                    toggleDoNotSendModal={toggleDoNotSendModal}
                    handleUserAction={handleUserAction}
                    loadingUserAction={loadingUserAction}
                />
            </ModalBody>
        </Modal>
    );
};

export default TableRowDetailModal;
